import { getGLBData } from '@proxies/getGLBData'
import { setSceneCostCatalog } from '@utils/debug/sceneCostProfiler'
import { loadGlbPreferCache } from '@utils/sceneObjects/glbLoaderCache'
import { Setter } from '@types'
import {
  getProjectFurnitureBatchSize,
  yieldToMainThread,
} from '../../viewport/readCapabilities'

export type ProjectCatalogJob = {
  key: string
  itemData: {
    type?: string
    catalogID?: string | number
    idUnidades?: string | number
    glbPath?: string
  }
}

const STRUCTURE_TYPES = new Set(['walls', 'partition', 'hiddenWall', 'holes', 'lights'])

export function isStructureProjectItem(item: { type?: string } | null | undefined): boolean {
  return Boolean(item?.type && STRUCTURE_TYPES.has(item.type))
}

export function itemNeedsCatalogGlb(item: {
  type?: string
  catalogID?: string | number
  idUnidades?: string | number
  glbPath?: string
} | null | undefined): boolean {
  if (!item) return false
  const toSearch = item.catalogID || item.idUnidades
  return Boolean(
    toSearch &&
      (item.glbPath ||
        item.type === 'linkedItem' ||
        item.type === 'linkedSon' ||
        item.type === 'items'),
  )
}

export function resolveCatalogGlbPath(
  savedPath: string | undefined,
  catalogData: { referenciaBlender?: string } | null | undefined,
): string | undefined {
  const fresh = catalogData?.referenciaBlender?.trim()
  if (fresh) return fresh
  const saved = typeof savedPath === 'string' ? savedPath.trim() : ''
  return saved || undefined
}

export function collectProjectCatalogJobs(
  items: Iterable<{
    type?: string
    catalogID?: string | number
    idUnidades?: string | number
    glbPath?: string
  }>,
  structure: boolean,
): ProjectCatalogJob[] {
  const seen = new Set<string>()
  const jobs: ProjectCatalogJob[] = []
  for (const itemData of items) {
    if (structure !== isStructureProjectItem(itemData)) continue
    if (!itemNeedsCatalogGlb(itemData)) continue
    const key = String(itemData.catalogID || itemData.idUnidades || '')
    if (!key || seen.has(key)) continue
    seen.add(key)
    jobs.push({ key, itemData })
  }
  return jobs
}

export function chunkCatalogJobs(jobs: ProjectCatalogJob[], size: number): ProjectCatalogJob[][] {
  const batches: ProjectCatalogJob[][] = []
  const step = Math.max(1, size)
  for (let i = 0; i < jobs.length; i += step) {
    batches.push(jobs.slice(i, i + step))
  }
  return batches
}

function syncCatalogGlbPath(
  job: ProjectCatalogJob,
  path: string,
  openedItems?: Map<string, any>,
) {
  job.itemData.glbPath = path
  if (!openedItems) return
  for (const item of openedItems.values()) {
    const key = String(item?.catalogID || item?.idUnidades || '')
    if (key === job.key) item.glbPath = path
  }
}

export async function loadProjectCatalogJob(
  job: ProjectCatalogJob,
  set: Setter,
  isAlreadyLoaded?: (catalogKey: string | number) => boolean,
  openedItems?: Map<string, any>,
): Promise<boolean> {
  const toSearch = job.itemData.catalogID || job.itemData.idUnidades
  if (!toSearch) return false
  if (isAlreadyLoaded?.(toSearch)) return true

  const apiStart = performance.now()
  const glbData = await getGLBData(toSearch)
  setSceneCostCatalog(String(toSearch), performance.now() - apiStart)
  const glbUrl = resolveCatalogGlbPath(
    job.itemData.glbPath,
    glbData as { referenciaBlender?: string },
  )
  if (!glbUrl) {
    console.error(`❌ Sin GLB (catálogo ni guardado) para ${toSearch}`)
    return false
  }
  if ((glbData as { referenciaBlender?: string })?.referenciaBlender) {
    syncCatalogGlbPath(
      job,
      (glbData as { referenciaBlender: string }).referenciaBlender,
      openedItems,
    )
  }

  try {
    const glb = await loadGlbPreferCache(glbUrl)
    set((store) => {
      if (store.sceneGLB.has(toSearch)) return
      store.sceneGLB.set(toSearch, {
        glb,
        backGLBInfo: glbData,
      })
    })
    return true
  } catch {
    console.error(`❌ GLB no disponible para ${toSearch}:`, glbUrl)
    return false
  }
}

export async function loadProjectCatalogJobs(
  jobs: ProjectCatalogJob[],
  set: Setter,
  isAlreadyLoaded?: (catalogKey: string | number) => boolean,
  openedItems?: Map<string, any>,
): Promise<void> {
  const batches = chunkCatalogJobs(jobs, getProjectFurnitureBatchSize())
  for (const batch of batches) {
    await Promise.all(
      batch.map((job) => loadProjectCatalogJob(job, set, isAlreadyLoaded, openedItems)),
    )
    await yieldToMainThread()
  }
}

export function filterItemsForOpenLayer(
  allItems: Map<string, any>,
  loadedFurnitureKeys: Set<string> | null,
): Map<string, any> {
  const next = new Map<string, any>()
  for (const [id, item] of allItems) {
    if (isStructureProjectItem(item)) {
      next.set(id, item)
      continue
    }
    if (loadedFurnitureKeys === null) continue
    if (item?.type === 'itemGroup') {
      next.set(id, item)
      continue
    }
    if (!itemNeedsCatalogGlb(item)) {
      next.set(id, item)
      continue
    }
    const key = String(item.catalogID || item.idUnidades || '')
    if (key && loadedFurnitureKeys.has(key)) next.set(id, item)
  }
  return next
}

export function filterBoundingBoxesForItems(
  allBoxes: Map<string, any>,
  includedItems: Map<string, any>,
): Map<string, any> {
  const next = new Map<string, any>()
  for (const [key, value] of allBoxes) {
    const itemId = key.startsWith('COBB_') ? key.slice('COBB_'.length) : key
    if (includedItems.has(itemId) || includedItems.has(key)) {
      next.set(key, value)
    }
  }
  return next
}
